import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, getDocs } from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { db } from "../Config/firebase";
import { abrirJanelaImpressao } from "../CalibracaoPrensa/impressaoCalibracaoPrensa";
import RelatorioCalibracaoPrensaSolosPrint from "./RelatorioCalibracaoPrensaSolosPrint";
import { consultaTenantAtual } from "../Utils/tenant";

const colecaoCalibracaoSolos = "tblCalibracaoPrensasSolos";

function normalizarDataISO(valor) {
  if (!valor) return "";
  if (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}/.test(valor)) return valor.slice(0, 10);
  if (typeof valor === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
    const [dia, mes, ano] = valor.split("/");
    return `${ano}-${mes}-${dia}`;
  }
  if (valor?.toDate) return normalizarDataISO(valor.toDate());
  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    const ano = valor.getFullYear();
    const mes = String(valor.getMonth() + 1).padStart(2, "0");
    const dia = String(valor.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
  }
  return "";
}

function calcularDataVencimentoISO(dataCalibracao, validade) {
  const dataBase = normalizarDataISO(dataCalibracao);
  const texto = String(validade || "").trim().toLowerCase().replace(",", ".");
  if (!dataBase || !texto) return "";
  const quantidade = Number(texto.match(/[\d.]+/)?.[0]);
  if (!Number.isFinite(quantidade) || quantidade <= 0) return "";

  const [ano, mes, dia] = dataBase.split("-").map(Number);
  const vencimento = new Date(ano, mes - 1, dia);
  if (texto.includes("mes")) vencimento.setMonth(vencimento.getMonth() + quantidade);
  else vencimento.setFullYear(vencimento.getFullYear() + quantidade);
  return normalizarDataISO(vencimento);
}

function formatarDataBR(valor) {
  const data = normalizarDataISO(valor);
  if (!data) return "-";
  const [ano, mes, dia] = data.split("-");
  return `${dia}/${mes}/${ano}`;
}

function extrairNumeroSequencialCodigo(codigo) {
  const texto = String(codigo || "");
  const match = texto.match(/RE-CAL-SOLOS\s*-\s*(\d+)\s*\/\s*\d{4}/i);
  const numero = Number(match?.[1] || texto.match(/\d+/)?.[0] || 0);
  return Number.isFinite(numero) ? numero : 0;
}

function obterStatusVencimento(dataVencimento) {
  const dataISO = normalizarDataISO(dataVencimento);
  if (!dataISO) return { chave: "sem-data", cor: "text.secondary", texto: "-" };

  const [ano, mes, dia] = dataISO.split("-").map(Number);
  const hoje = new Date();
  const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const vencimento = new Date(ano, mes - 1, dia);
  const diasRestantes = Math.ceil((vencimento.getTime() - inicioHoje.getTime()) / 86400000);

  if (diasRestantes < 0) return { chave: "vencido", cor: "error.main", texto: formatarDataBR(dataISO) };
  if (diasRestantes <= 30) return { chave: "faltando-30-dias", cor: "warning.main", texto: formatarDataBR(dataISO) };
  return { chave: "em-dia", cor: "success.main", texto: formatarDataBR(dataISO) };
}

export default function ListaCalibracoesPrensaSolos() {
  const history = useHistory();
  const [calibracoes, setCalibracoes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [modoPreview, setModoPreview] = useState(false);
  const [calibracaoPreview, setCalibracaoPreview] = useState(null);

  useEffect(() => {
    carregarCalibracoes();
  }, []);

  async function carregarCalibracoes() {
    const snap = await getDocs(consultaTenantAtual(collection(db, colecaoCalibracaoSolos)));
    const lista = snap.docs
      .map((item) => {
        const dados = item.data();
        return {
          id: item.id,
          ...dados,
          dataVencimento:
            dados.dataVencimento || calcularDataVencimentoISO(dados.dataCalibracao, dados.validade)
        };
      })
      .sort((a, b) => {
        const numeroA = extrairNumeroSequencialCodigo(a.codigo);
        const numeroB = extrairNumeroSequencialCodigo(b.codigo);
        if (numeroA !== numeroB) return numeroB - numeroA;
        return normalizarDataISO(b.dataCalibracao).localeCompare(normalizarDataISO(a.dataCalibracao));
      });

    setCalibracoes(lista);
  }

  function imprimir(dados) {
    const html = ReactDOMServer.renderToStaticMarkup(
      <RelatorioCalibracaoPrensaSolosPrint dados={dados} />
    );

    abrirJanelaImpressao({
      title: "Certificado de Calibração de Prensa CBR Marshall",
      html,
      printStyles: "@page { size: A4 portrait; margin: 5mm; }"
    });
  }

  const listaFiltrada = calibracoes.filter((item) => {
    const termo = filtro.trim().toLowerCase();
    const correspondeTexto =
      !termo ||
      contemTextoPesquisa(String(item.codigo || "").toLowerCase(), termo) ||
      contemTextoPesquisa(String(item.empresa || "").toLowerCase(), termo) ||
      contemTextoPesquisa(String(item.serie || "").toLowerCase(), termo) ||
      contemTextoPesquisa(String(item.tipoEquipamento || "").toLowerCase(), termo);

    if (!correspondeTexto) return false;
    if (filtroStatus === "todos") return true;
    return obterStatusVencimento(item.dataVencimento).chave === filtroStatus;
  });

  if (modoPreview && calibracaoPreview) {
    return (
      <RelatorioCalibracaoPrensaSolosPrint
        dados={calibracaoPreview}
        onVoltar={() => {
          setModoPreview(false);
          setCalibracaoPreview(null);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: 4 }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Calibração de Prensas - Solos</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Lista de calibrações CBR / Marshall cadastradas</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Button variant="contained" onClick={() => history.push("/app/calibracao-prensa-solos/novo")}>
            + Nova Calibração
          </Button>
          <TextField
            size="small"
            label="Pesquisar"
            placeholder="Código, empresa, série ou tipo"
            value={filtro}
            onChange={(event) => setFiltro(event.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={filtroStatus}
            onChange={(event) => setFiltroStatus(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="vencido">Vencido</MenuItem>
            <MenuItem value="faltando-30-dias">Faltando 30 dias</MenuItem>
          </TextField>
        </Stack>
        </Paper>

        <Paper>
          <Table className="lista-cadastro-padrao" size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center"><strong>Código</strong></TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Equipamento</TableCell>
                <TableCell>Série</TableCell>
                <TableCell>Data Calibração</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listaFiltrada.map((item) => {
                const status = obterStatusVencimento(item.dataVencimento);

                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => history.push(`/app/calibracao-prensa-solos/${item.id}`)}
                  >
                    <TableCell align="center">{item.codigo || "-"}</TableCell>
                    <TableCell className="lista-dado-principal">{item.empresa || "-"}</TableCell>
                    <TableCell className="lista-dado-destaque">{item.tipoEquipamento || "-"}</TableCell>
                    <TableCell className="lista-dado-secundario">{item.serie || "-"}</TableCell>
                    <TableCell className="lista-dado-secundario">
                      <Typography variant="body2">{formatarDataBR(item.dataCalibracao)}</Typography>
                      <Typography variant="caption" sx={{ color: status.cor, fontWeight: 700 }}>
                        Vence em {status.texto}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Visualizar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              setCalibracaoPreview(item);
                              setModoPreview(true);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              history.push(`/app/calibracao-prensa-solos/${item.id}`);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clonar certificado">
                          <IconButton size="small" aria-label="Clonar certificado" onClick={(event) => {
                            event.stopPropagation();
                            history.push(`/app/calibracao-prensa-solos/novo?clonar=${encodeURIComponent(item.id)}`);
                          }}>
                            <ContentCopyIcon sx={{ color: "#0288d1" }} fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <HistoricoCadastro colecao={colecaoCalibracaoSolos} documentoId={item.id} registro={item} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </>
  );
}
